import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Play, Trash2, Edit2, Megaphone, CheckCircle, Clock, AlertCircle, FileText, ShieldOff, History, CalendarClock, X, Webhook, Mail, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, CardHeader, CardTitle,
  Button, Badge, Grid, Flex, Label, FormGroup, Input,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter,
  EmptyStateAction, Spinner, LoadingContainer
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';
import CampaignWizard from '../components/campaigns/CampaignWizard';

const STATUS_ICONS = {
  draft: FileText, running: Clock, completed: CheckCircle, failed: AlertCircle, scheduled: CalendarClock,
};
const STATUS_VARIANTS = {
  draft: 'neutral', running: 'warning', completed: 'success', failed: 'danger', scheduled: 'info',
};
const CHANNEL_EMOJI = { whatsapp: '📱', email: '📧', telegram: '✈️' };

export default function CampaignsPage() {
  const { t } = useTranslation('common');
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const toast   = useToast();
  const confirm = useConfirm();

  const [campaigns, setCampaigns]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [connectors, setConnectors]   = useState([]);
  const [agents, setAgents]           = useState([]);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [mktTemplates, setMktTemplates]     = useState([]);
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);
  const [launching, setLaunching]     = useState(null);
  const [dryRunning, setDryRunning]   = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [activeTab, setActiveTab]     = useState('active'); // 'active' | 'history'
  const [scheduling, setScheduling]   = useState(null);  // campanha sendo agendada
  const [scheduleForm, setScheduleForm] = useState({ mode: 'once', scheduledAt: '', cronExpression: '' });
  const [viewing, setViewing]         = useState(null);  // campanha em modo read-only
  const [executionLogs, setExecutionLogs] = useState([]);
  const [responseTotals, setResponseTotals] = useState({});  // execution logs for passive campaigns

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const breadcrumbItems = [
    isSallusAdmin
      ? { label: 'Libben Agents', path: '/sallus/dashboard' }
      : { label: t('home'), path: `/dashboard/${tenantId}` },
    { label: t('campaigns.title'), icon: '📣' }
  ];

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [campRes, connRes, agentRes, ctRes, mktRes] = await Promise.all([
        api.get(`/campaigns/${tenantId}`).catch(() => ({ data: { campaigns: [] } })),
        api.get(`/connectors/${tenantId}`).catch(() => ({ data: { connectors: [] } })),
        api.get(`/marketplace/tenants/${tenantId}/agents`).catch(() => ({ data: { agents: [] } })),
        api.get('/marketplace/connector-types').catch(() => ({ data: { connectorTypes: [] } })),
        api.get(`/mktpl/${tenantId}`).catch(() => ({ data: { templates: [] } })),
      ]);
      const allCampaigns = campRes.data.campaigns || [];
      setCampaigns(allCampaigns);
      setConnectors(connRes.data.connectors || []);
      setAgents(agentRes.data.agents || []);
      setConnectorTypes(ctRes.data.connectorTypes || []);
      setMktTemplates(mktRes.data.templates || []);

      // Fetch execution logs and response totals for all campaigns
      const campaignsWithExecs = allCampaigns;
      if (campaignsWithExecs.length > 0) {
        const [execResults, respResults] = await Promise.all([
          Promise.all(campaignsWithExecs.map(c =>
            api.get(`/campaigns/${tenantId}/${c.campaignId}/executions`).catch(() => ({ data: { executions: [] } }))
          )),
          Promise.all(campaignsWithExecs.map(c =>
            api.get(`/campaigns/${tenantId}/${c.campaignId}/responses`).catch(() => ({ data: { total: 0 } }))
          )),
        ]);
        const allExecs = [];
        const respTotals = {};
        campaignsWithExecs.forEach((c, i) => {
          const execs = execResults[i].data.executions || [];
          execs.forEach(ex => allExecs.push({ ...ex, campaignName: c.name, instanceId: c.instanceId, channels: c.channels }));
          respTotals[c.campaignId] = respResults[i].data.total || 0;
        });
        setExecutionLogs(allExecs);
        setResponseTotals(respTotals);
      } else {
        setExecutionLogs([]);
        setResponseTotals({});
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const activeCampaigns  = useMemo(() => campaigns.filter(c => ['draft','running','scheduled'].includes(c.status) || c.scheduleStatus === 'scheduled'), [campaigns]);
  const historyCampaigns = useMemo(() => {
    // Manual campaigns that completed/failed
    // Campaigns that completed/failed the old way (status-based) — legacy
    const manualHistory = campaigns.filter(c => ['completed','failed'].includes(c.status))
      .map(c => ({ ...c, _type: 'campaign', _sortDate: c.launchedAt || c.createdAt || '' }));

    // Execution logs from all campaigns as individual history rows
    const execHistory = executionLogs
      .map(ex => ({
        _type: 'execution',
        _sortDate: ex.timestamp || '',
        campaignId: ex.campaignId,
        name: ex.campaignName || ex.campaignId,
        instanceId: ex.instanceId,
        channels: ex.channels,
        status: ex.result === 'success' ? 'completed' : (ex.result || 'failed'),
        launchedAt: ex.timestamp,
        stats: ex.stats || { total: ex.contactCount || 0, sent: ex.contactCount || 0 },
        source: ex.source || 'endpoint',
      }));

    return [...manualHistory, ...execHistory]
      .sort((a, b) => (b._sortDate || '').localeCompare(a._sortDate || ''));
  }, [campaigns, executionLogs]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setShowForm(true); };

  const handleSave = async (formData) => {
    try {
      let savedId;
      if (editing) {
        await api.put(`/campaigns/${tenantId}/${editing.campaignId}`, formData);
        savedId = editing.campaignId;
        toast.success(t('campaigns.updated'));
      } else {
        const res = await api.post(`/campaigns/${tenantId}`, formData);
        savedId = res.data.campaign?.campaignId;
        toast.success(t('campaigns.created'));
      }
      // Agendamento opcional
      if (savedId && formData.schedule?.enabled) {
        const schedPayload = formData.schedule.mode === 'once'
          ? { scheduledAt: new Date(formData.schedule.scheduledAt).toISOString() }
          : { cronExpression: formData.schedule.cronExpression };
        await api.post(`/campaigns/${tenantId}/${savedId}/schedule`, schedPayload);
        toast.success(t('campaigns.scheduleSuccess'));
      } else if (editing && !formData.schedule?.enabled && editing.scheduleStatus === 'scheduled') {
        await api.delete(`/campaigns/${tenantId}/${savedId}/schedule`).catch(() => {});
      }
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleDelete = async (c) => {
    const ok = await confirm({ title: t('campaigns.deleteConfirm', { name: c.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/campaigns/${tenantId}/${c.campaignId}`);
      toast.success(t('campaigns.deleted'));
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleLaunch = async (c) => {
    const ok = await confirm({ title: t('campaigns.launchConfirm', { name: c.name }), message: t('campaigns.launchConfirmMsg') });
    if (!ok) return;
    setLaunching(c.campaignId);
    try {
      const res = await api.post(`/campaigns/${tenantId}/${c.campaignId}/launch`);
      if (res.status === 202) {
        // Async launch — poll for completion
        toast.success(t('campaigns.launchStarted'));
        const pollResult = await pollCampaignStatus(c.campaignId, 'status', 'running');
        if (pollResult) {
          toast.success(t('campaigns.launchSuccess', { sent: pollResult.stats?.sent || 0, followUps: pollResult.stats?.followUps || 0 }));
        }
        load();
      } else {
        toast.success(t('campaigns.launchSuccess', { sent: res.data.sent, followUps: res.data.followUps }));
        load();
      }
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setLaunching(null); }
  };

  const handleDryRun = async (c) => {
    setDryRunning(c.campaignId);
    setDryRunResult(null);
    try {
      const res = await api.post(`/campaigns/${tenantId}/${c.campaignId}/dry-run`);
      if (res.status === 202) {
        // Async dry-run — poll for result
        const pollResult = await pollCampaignStatus(c.campaignId, 'dryRunStatus', 'running');
        if (pollResult?.dryRunResult) {
          setDryRunResult(pollResult.dryRunResult);
        } else {
          toast.error(t('campaigns.dryRunNoResult'));
        }
      } else {
        setDryRunResult(res.data);
      }
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    finally { setDryRunning(null); }
  };

  // Polls campaign until a field changes from a given value (or timeout)
  const pollCampaignStatus = async (campaignId, field, waitWhile, maxAttempts = 30) => {
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000)); // 2s between polls
      try {
        const res = await api.get(`/campaigns/${tenantId}/${campaignId}`);
        const campaign = res.data.campaign || res.data;
        if (campaign[field] !== waitWhile) return campaign;
      } catch { /* ignore polling errors */ }
    }
    return null;
  };

  const openSchedule = (c) => {
    setScheduling(c);
    setScheduleForm({ mode: 'once', scheduledAt: '', cronExpression: '' });
  };

  const handleSchedule = async () => {
    if (!scheduling) return;
    const payload = scheduleForm.mode === 'once'
      ? { scheduledAt: new Date(scheduleForm.scheduledAt).toISOString() }
      : { cronExpression: scheduleForm.cronExpression };
    try {
      await api.post(`/campaigns/${tenantId}/${scheduling.campaignId}/schedule`, payload);
      toast.success(t('campaigns.scheduleSuccess'));
      setScheduling(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const handleCancelSchedule = async (c) => {
    const ok = await confirm({ title: t('campaigns.cancelScheduleConfirm', { name: c.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/campaigns/${tenantId}/${c.campaignId}/schedule`);
      toast.success(t('campaigns.scheduleCancelled'));
      load();
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const getCampaignChannels = (c) => {
    if (c.channels && Object.keys(c.channels).length > 0) {
      return Object.entries(c.channels)
        .filter(([, cfg]) => cfg.enabled)
        .map(([k]) => CHANNEL_EMOJI[k] || k)
        .join(' ');
    }
    const conn = connectors.find(x => x.connectorId === c.connectorId);
    return conn ? `${CHANNEL_EMOJI[conn.type] || ''} ${conn.name}` : '—';
  };

  if (loading) return (
    <>
      <AppHeader title={t('campaigns.title')} />
      <PageContainer><LoadingContainer><Spinner /><span>{t('loading')}</span></LoadingContainer></PageContainer>
    </>
  );

  return (
    <>
      <AppHeader title={t('campaigns.title')} />
      <PageContainer>
        <Breadcrumb items={breadcrumbItems} />
        <PageHeader>
          <div>
            <PageTitle>📣 {t('campaigns.titleFull')}</PageTitle>
            <PageSubtitle>{t('campaigns.subtitle')}</PageSubtitle>
          </div>
          <Button variant="primary" size="sm" onClick={openCreate}><Plus size={14} /> {t('campaigns.new')}</Button>
          <Button variant="secondary" size="sm" onClick={() => navigate(`/optout/${tenantId}`)} title={t('campaigns.blacklist')}>
            <ShieldOff size={14} /> {t('campaigns.blacklist')}
          </Button>
        </PageHeader>

        <TabBar>
          <Tab $active={activeTab === 'active'} onClick={() => setActiveTab('active')}>
            <Megaphone size={13} /> {t('campaigns.tabActive')} <TabCount>{activeCampaigns.length}</TabCount>
          </Tab>
          <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
            <History size={13} /> {t('campaigns.tabHistory')} <TabCount>{historyCampaigns.length}</TabCount>
          </Tab>
        </TabBar>

        {activeTab === 'active' && (
          activeCampaigns.length === 0 ? (
          <EmptyStateAction
            icon={<Megaphone size={48} strokeWidth={1} />}
            title={t('campaigns.empty')}
            action={openCreate}
            actionLabel={t('campaigns.newFirst')}
          />
        ) : (
          <Grid min="320px">
            {activeCampaigns.map(c => {
              const triggerType = c.triggerConfig?.type || 'manual';
              const isPassiveTrigger = triggerType === 'endpoint' || triggerType === 'email';

              // For endpoint/email campaigns stuck in "draft", show trigger type instead
              let Icon, variant, label;
              if (isPassiveTrigger && c.status === 'draft') {
                if (triggerType === 'endpoint') {
                  Icon = Webhook;
                  variant = 'info';
                  label = t('campaigns.triggerType.endpoint', { defaultValue: 'Webhook ativo' });
                } else {
                  Icon = Mail;
                  variant = 'info';
                  label = t('campaigns.triggerType.email', { defaultValue: 'Email ativo' });
                }
              } else {
                Icon = STATUS_ICONS[c.status] || STATUS_ICONS.draft;
                variant = STATUS_VARIANTS[c.status] || 'neutral';
                label = t(`campaigns.status.${c.status}`, { defaultValue: c.status });
              }
              return (
                <Card key={c.campaignId} onClick={() => setViewing(c)} style={{ cursor: 'pointer' }}>
                  <CardHeader>
                    <Flex gap={theme.spacing[2]} style={{ alignItems: 'center' }}>
                      <Megaphone size={16} color={theme.colors.primary} />
                      <CardTitle style={{ flex: 1 }}>{c.name}</CardTitle>
                      <Badge variant={variant}><Icon size={10} style={{ marginRight: 4 }} />{label}</Badge>
                    </Flex>
                  </CardHeader>
                  <MetaRow>
                    <MetaItem>🤖 {agents.find(a => a.instanceId === c.instanceId)?.name || c.instanceId}</MetaItem>
                    <MetaItem>📡 {getCampaignChannels(c)}</MetaItem>
                    {c.agentFollowUpEnabled === false && <MetaItem>🔕 {t('campaigns.noFollowUp')}</MetaItem>}
                  </MetaRow>
                  {triggerType === 'endpoint' && c.triggerUrl && (
                    <EndpointUrlRow>
                      <Webhook size={12} />
                      <EndpointUrlText>{`${process.env.REACT_APP_API_URL || ''}${c.triggerUrl}`}</EndpointUrlText>
                      <EndpointCopyBtn
                        onClick={() => {
                          navigator.clipboard.writeText(`${process.env.REACT_APP_API_URL || ''}${c.triggerUrl}`);
                        }}
                        title={t('campaigns.copyUrl', { defaultValue: 'Copiar URL' })}
                      >
                        <Copy size={11} />
                      </EndpointCopyBtn>
                    </EndpointUrlRow>
                  )}
                  {(c.stats || c.totalStats) && (
                    <StatsRow>
                      {c.totalStats ? (
                        <>
                          <StatChip>✅ {c.totalStats.totalSent} {t('campaigns.sent')}</StatChip>
                          <StatChip>🔄 {c.totalStats.executions} {t('campaigns.executions', { defaultValue: 'execuções' })}</StatChip>
                          {c.totalStats.totalErrors > 0 && <StatChip $danger>⚠ {c.totalStats.totalErrors} {t('campaigns.errors')}</StatChip>}
                        </>
                      ) : (
                        <>
                          <StatChip>✅ {c.stats.sent} {t('campaigns.sent')}</StatChip>
                          {c.stats.skipped > 0 && <StatChip>⏭ {c.stats.skipped} {t('campaigns.skipped')}</StatChip>}
                          {c.stats.optouts > 0 && <StatChip $warn>🚫 {c.stats.optouts} opt-out</StatChip>}
                          {c.stats.errors > 0 && <StatChip $danger>⚠ {c.stats.errors} {t('campaigns.errors')}</StatChip>}
                        </>
                      )}
                    </StatsRow>
                  )}
                  <Flex gap={theme.spacing[2]} style={{ marginTop: theme.spacing[3] }} onClick={(e) => e.stopPropagation()}>
                    {c.scheduleStatus === 'scheduled' ? (
                      <>
                        <ScheduledInfo>
                          <CalendarClock size={12} />
                          {c.cronExpression
                            ? t('campaigns.recurrentLabel', { expr: c.cronExpression, defaultValue: 'Recorrente: {{expr}}' })
                            : t('campaigns.scheduledLabel', { date: new Date(c.scheduledAt).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }), defaultValue: 'Agendado: {{date}}' })
                          }
                          {c.cronExpression && c.scheduledAt && (
                            <NextRunLabel>
                              {t('campaigns.nextRunLabel', { date: new Date(c.scheduledAt).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }), defaultValue: 'Próximo: {{date}}' })}
                            </NextRunLabel>
                          )}
                        </ScheduledInfo>
                        <Button variant="danger" size="sm" onClick={() => handleCancelSchedule(c)} title={t('campaigns.cancelSchedule')}>
                          <X size={12} />
                        </Button>
                      </>
                    ) : c.status === 'draft' ? (
                      <>
                        {(!c.triggerConfig || c.triggerConfig.type === 'manual' || c.triggerConfig.type === 'scheduled') && (
                          <>
                            <Button variant="primary" size="sm" style={{ flex: 1 }}
                              onClick={() => handleLaunch(c)} disabled={launching === c.campaignId}>
                              {launching === c.campaignId ? <Spinner size={12} /> : <Play size={12} />}
                              {' '}{launching === c.campaignId ? t('campaigns.launching') : t('campaigns.launch')}
                            </Button>
                            <Button variant="secondary" size="sm"
                              onClick={() => handleDryRun(c)} disabled={dryRunning === c.campaignId}
                              title={t('campaigns.dryRunTitle', { defaultValue: 'Simular sem enviar' })}>
                              {dryRunning === c.campaignId ? <Spinner size={12} /> : '🧪'}
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => openSchedule(c)} title={t('campaigns.schedule')}>
                              <CalendarClock size={12} />
                            </Button>
                          </>
                        )}
                        <Button variant="secondary" size="sm" onClick={() => openEdit(c)}><Edit2 size={12} /></Button>
                        <Button variant="danger" size="sm" onClick={() => handleDelete(c)}><Trash2 size={12} /></Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" style={{ flex: 1 }} disabled>
                        <Clock size={12} /> {t('campaigns.running')}
                      </Button>
                    )}
                  </Flex>
                </Card>
              );
            })}
          </Grid>
        ))}

        {activeTab === 'history' && (
          historyCampaigns.length === 0 ? (
            <EmptyStateAction
              icon={<History size={48} strokeWidth={1} />}
              title={t('campaigns.historyEmpty')}
            />
          ) : (
            <HistoryTable>
              <thead>
                <tr>
                  <HTh>{t('campaigns.histDate')}</HTh>
                  <HTh>{t('campaigns.histName')}</HTh>
                  <HTh>{t('campaigns.histChannel')}</HTh>
                  <HTh>{t('campaigns.histAgent')}</HTh>
                  <HTh style={{ textAlign: 'right' }}>{t('campaigns.histTotal')}</HTh>
                  <HTh style={{ textAlign: 'right' }}>✅ {t('campaigns.sent')}</HTh>
                  <HTh style={{ textAlign: 'right' }}>💬 {t('campaigns.responses')}</HTh>
                  <HTh style={{ textAlign: 'right' }}>🚫 Opt-out</HTh>
                  <HTh style={{ textAlign: 'right' }}>⚠ {t('campaigns.errors')}</HTh>
                  <HTh style={{ textAlign: 'right' }}>{t('campaigns.histDuration')}</HTh>
                  <HTh>{t('campaigns.histStatus')}</HTh>
                  <HTh />
                </tr>
              </thead>
              <tbody>
                {historyCampaigns.map((c, idx) => {
                  const duration = c.launchedAt && c.completedAt
                    ? formatDuration(new Date(c.completedAt) - new Date(c.launchedAt))
                    : '—';
                  const Icon    = STATUS_ICONS[c.status] || STATUS_ICONS.draft;
                  const variant = STATUS_VARIANTS[c.status] || 'neutral';
                  return (
                    <tr key={`${c.campaignId}-${idx}`} onClick={() => setViewing(c)} style={{ cursor: 'pointer' }}>
                      <HTd style={{ whiteSpace: 'nowrap', fontSize: theme.typography.xs }}>
                        {c.launchedAt ? new Date(c.launchedAt).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                      </HTd>
                      <HTd style={{ fontWeight: theme.typography.medium }}>{c.name}</HTd>
                      <HTd>{getCampaignChannels(c)}</HTd>
                      <HTd style={{ fontSize: theme.typography.xs, color: theme.colors.textSecondary }}>
                        {agents.find(a => a.instanceId === c.instanceId)?.name || '—'}
                      </HTd>
                      <HTd style={{ textAlign: 'right' }}>{c.stats?.total ?? '—'}</HTd>
                      <HTd style={{ textAlign: 'right', color: theme.colors.success }}>{c.stats?.sent ?? '—'}</HTd>
                      <HTd style={{ textAlign: 'right' }}>{c._type === 'execution' ? (responseTotals[c.campaignId] || '—') : (c.stats?.followUps || '—')}</HTd>
                      <HTd style={{ textAlign: 'right', color: c.stats?.optouts > 0 ? theme.colors.warning : undefined }}>
                        {c.stats?.optouts ?? '—'}
                      </HTd>
                      <HTd style={{ textAlign: 'right', color: c.stats?.errors > 0 ? theme.colors.danger : undefined }}>
                        {c.stats?.errors ?? '—'}
                      </HTd>
                      <HTd style={{ textAlign: 'right', fontSize: theme.typography.xs, color: theme.colors.textMuted }}>{duration}</HTd>
                      <HTd>
                        <Badge variant={variant}><Icon size={10} style={{ marginRight: 4 }} />{t(`campaigns.status.${c.status}`, { defaultValue: c.status })}</Badge>
                      </HTd>
                      <HTd>
                        {(!c.triggerConfig || c.triggerConfig.type === 'manual' || c.triggerConfig.type === 'scheduled') && (
                          <Button variant="primary" size="sm"
                            onClick={(e) => { e.stopPropagation(); handleLaunch(c); }} disabled={launching === c.campaignId}
                            title={t('campaigns.relaunch')}>
                            {launching === c.campaignId ? <Spinner size={12} /> : <Play size={12} />}
                          </Button>
                        )}
                      </HTd>
                    </tr>
                  );
                })}
              </tbody>
            </HistoryTable>
          )
        )}

        {dryRunResult && (
          <ModalOverlay onClick={() => setDryRunResult(null)}>
            <ModalBox maxWidth="520px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>🧪 {t('campaigns.dryRunResultTitle', { defaultValue: 'Resultado da simulação' })}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setDryRunResult(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <DryRunGrid>
                  <DryRunStat>
                    <DryRunStatValue>{dryRunResult.totalContacts}</DryRunStatValue>
                    <DryRunStatLabel>{t('campaigns.dryRunTotal', { defaultValue: 'Contatos encontrados' })}</DryRunStatLabel>
                  </DryRunStat>
                  <DryRunStat>
                    <DryRunStatValue $color="success">{dryRunResult.valid}</DryRunStatValue>
                    <DryRunStatLabel>{t('campaigns.dryRunValid', { defaultValue: 'Seriam enviados' })}</DryRunStatLabel>
                  </DryRunStat>
                  <DryRunStat>
                    <DryRunStatValue $color="warning">{dryRunResult.skipped}</DryRunStatValue>
                    <DryRunStatLabel>{t('campaigns.dryRunSkipped', { defaultValue: 'Pulados' })}</DryRunStatLabel>
                  </DryRunStat>
                  <DryRunStat>
                    <DryRunStatValue $color="danger">{dryRunResult.optouts}</DryRunStatValue>
                    <DryRunStatLabel>{t('campaigns.dryRunOptouts', { defaultValue: 'Opt-out' })}</DryRunStatLabel>
                  </DryRunStat>
                </DryRunGrid>

                {dryRunResult.resolvedParams && Object.keys(dryRunResult.resolvedParams).length > 0 && (
                  <DryRunSection>
                    <DryRunSectionTitle>{t('campaigns.dryRunParams', { defaultValue: 'Parâmetros resolvidos' })}</DryRunSectionTitle>
                    <DryRunCode>{JSON.stringify(dryRunResult.resolvedParams, null, 2)}</DryRunCode>
                  </DryRunSection>
                )}

                {dryRunResult.sample?.length > 0 && (
                  <DryRunSection>
                    <DryRunSectionTitle>{t('campaigns.dryRunSample', { defaultValue: 'Amostra de destinatários' })}</DryRunSectionTitle>
                    {dryRunResult.sample.map((s, i) => (
                      <DryRunSampleRow key={i}>
                        <span>{s.name}</span>
                        <span style={{ color: theme.colors.textMuted, fontFamily: 'monospace', fontSize: 11 }}>{s.contact}</span>
                      </DryRunSampleRow>
                    ))}
                  </DryRunSection>
                )}
              </ModalBody>
            </ModalBox>
          </ModalOverlay>
        )}

        {scheduling && (
          <ModalOverlay onClick={() => setScheduling(null)}>
            <ModalBox maxWidth="480px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle><CalendarClock size={16} /> {t('campaigns.scheduleTitle', { name: scheduling.name })}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setScheduling(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <ModeToggle>
                  <ModeBtn $active={scheduleForm.mode === 'once'} onClick={() => setScheduleForm(p => ({ ...p, mode: 'once' }))}>
                    📅 {t('campaigns.scheduleOnce')}
                  </ModeBtn>
                  <ModeBtn $active={scheduleForm.mode === 'recurrent'} onClick={() => setScheduleForm(p => ({ ...p, mode: 'recurrent' }))}>
                    🔁 {t('campaigns.scheduleRecurrent')}
                  </ModeBtn>
                </ModeToggle>

                {scheduleForm.mode === 'once' ? (
                  <FormGroup>
                    <Label>{t('campaigns.scheduleDate')} *</Label>
                    <Input
                      type="datetime-local"
                      value={scheduleForm.scheduledAt}
                      onChange={e => setScheduleForm(p => ({ ...p, scheduledAt: e.target.value }))}
                      min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    />
                  </FormGroup>
                ) : (
                  <FormGroup>
                    <Label>{t('campaigns.cronExpression')} *</Label>
                    <Input
                      value={scheduleForm.cronExpression}
                      onChange={e => setScheduleForm(p => ({ ...p, cronExpression: e.target.value }))}
                      placeholder="rate(1 day) ou cron(0 9 * * MON-FRI *)"
                    />
                    <FieldHint>{t('campaigns.cronHint')}</FieldHint>
                    <CronExamples>
                      {[
                        { label: t('campaigns.cronEx.daily9', { defaultValue: 'Todo dia às 9h' }), expr: 'cron(0 9 * * * *)' },
                        { label: t('campaigns.cronEx.weekdays8', { defaultValue: 'Seg-Sex às 8h' }), expr: 'cron(0 8 * * MON-FRI *)' },
                        { label: t('campaigns.cronEx.hourly', { defaultValue: 'A cada hora' }), expr: 'rate(1 hour)' },
                        { label: t('campaigns.cronEx.every30', { defaultValue: 'A cada 30 min' }), expr: 'rate(30 minutes)' },
                        { label: t('campaigns.cronEx.mon10', { defaultValue: 'Toda segunda às 10h' }), expr: 'cron(0 10 * * MON *)' },
                        { label: t('campaigns.cronEx.monthly1', { defaultValue: 'Dia 1 de cada mês' }), expr: 'cron(0 9 1 * * *)' },
                      ].map(ex => (
                        <CronChip key={ex.expr} onClick={() => setScheduleForm(p => ({ ...p, cronExpression: ex.expr }))}>
                          {ex.label}
                        </CronChip>
                      ))}
                    </CronExamples>
                  </FormGroup>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setScheduling(null)}>{t('cancel')}</Button>
                <Button variant="primary" onClick={handleSchedule}>
                  <CalendarClock size={13} /> {t('campaigns.scheduleConfirm')}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {viewing && (
          <ModalOverlay onClick={() => setViewing(null)}>
            <ModalBox maxWidth="600px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle><Megaphone size={16} /> {viewing.name}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setViewing(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <DetailGrid>
                  <DetailRow>
                    <DetailLabel>{t('campaigns.histAgent')}</DetailLabel>
                    <DetailValue>🤖 {agents.find(a => a.instanceId === viewing.instanceId)?.name || viewing.instanceId}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>{t('campaigns.histChannel')}</DetailLabel>
                    <DetailValue>📡 {getCampaignChannels(viewing)}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Trigger</DetailLabel>
                    <DetailValue>{viewing.triggerConfig?.type || 'manual'}</DetailValue>
                  </DetailRow>
                  {viewing.triggerConfig?.type === 'endpoint' && (
                    <>
                      <DetailRow>
                        <DetailLabel>Endpoint URL</DetailLabel>
                        <DetailValueMono>
                          {`${process.env.REACT_APP_API_URL || ''}${viewing.triggerUrl || `/campaigns/${tenantId}/${viewing.campaignId}/trigger`}`}
                        </DetailValueMono>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>Verify Token</DetailLabel>
                        <DetailValueMono>{viewing.triggerConfig.verifyToken || '—'}</DetailValueMono>
                      </DetailRow>
                      <DetailRow>
                        <DetailLabel>{t('campaigns.authHeader', { defaultValue: 'Header de autenticação' })}</DetailLabel>
                        <DetailValueMono>token: {viewing.triggerConfig.verifyToken || '<token>'}</DetailValueMono>
                      </DetailRow>
                    </>
                  )}
                  {viewing.triggerConfig?.type === 'email' && viewing.triggerConfig?.authorizedEmails?.length > 0 && (
                    <DetailRow>
                      <DetailLabel>{t('campaigns.authorizedEmails', { defaultValue: 'Emails autorizados' })}</DetailLabel>
                      <DetailValue>{viewing.triggerConfig.authorizedEmails.join(', ')}</DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>{t('campaigns.agenticResponse', { defaultValue: 'Resposta agêntica' })}</DetailLabel>
                    <DetailValue>{viewing.agentFollowUpEnabled !== false ? t('campaigns.agenticOn', { defaultValue: '✅ Ativo' }) : t('campaigns.agenticOff', { defaultValue: '🔕 Desativado' })}</DetailValue>
                  </DetailRow>
                  {viewing.dataSource?.connectorId && (
                    <DetailRow>
                      <DetailLabel>{t('campaigns.dataSource', { defaultValue: 'Fonte de dados' })}</DetailLabel>
                      <DetailValue>
                        {connectors.find(c => c.connectorId === viewing.dataSource.connectorId)?.name || viewing.dataSource.connectorId}
                        {viewing.dataSource.toolName && ` → ${viewing.dataSource.toolName}`}
                      </DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>Rate limit</DetailLabel>
                    <DetailValue>{viewing.rateLimit?.batchSize || 30} {t('campaigns.contactsPerBatch', { defaultValue: 'contatos/lote' })}{viewing.rateLimit?.delayMs > 0 ? `, ${viewing.rateLimit.delayMs}ms delay` : ''}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>{t('campaigns.createdAt', { defaultValue: 'Criada em' })}</DetailLabel>
                    <DetailValue>{viewing.createdAt ? new Date(viewing.createdAt).toLocaleString('pt-BR') : '—'}</DetailValue>
                  </DetailRow>
                  {viewing.totalStats && (
                    <DetailRow>
                      <DetailLabel>{t('campaigns.cumulativeTotal', { defaultValue: 'Total acumulado' })}</DetailLabel>
                      <DetailValue>
                        ✅ {viewing.totalStats.totalSent} {t('campaigns.sent')} · 🔄 {viewing.totalStats.executions} {t('campaigns.executions', { defaultValue: 'execuções' })}
                        {viewing.totalStats.totalErrors > 0 && ` · ⚠ ${viewing.totalStats.totalErrors} ${t('campaigns.errors')}`}
                      </DetailValue>
                    </DetailRow>
                  )}
                  {viewing.lastExecutionAt && (
                    <DetailRow>
                      <DetailLabel>{t('campaigns.lastExecution', { defaultValue: 'Última execução' })}</DetailLabel>
                      <DetailValue>{new Date(viewing.lastExecutionAt).toLocaleString('pt-BR')}</DetailValue>
                    </DetailRow>
                  )}
                </DetailGrid>
                <CampaignResponsesPanel tenantId={tenantId} campaignId={viewing.campaignId} />
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setViewing(null)}>{t('close', { defaultValue: 'Fechar' })}</Button>
                <Button variant="primary" onClick={() => { setViewing(null); openEdit(viewing); }}>
                  <Edit2 size={13} /> {t('campaigns.edit', { defaultValue: 'Editar' })}
                </Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        <CampaignWizard
          key={editing ? `edit-${editing.campaignId}` : `new-${showForm}`}
          visible={showForm}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={async (formData) => {
            await handleSave(formData);
            setShowForm(false);
            setEditing(null);
          }}
          initialData={editing}
          tenantId={tenantId}
          agents={agents}
          connectors={connectors}
          connectorTypes={connectorTypes}
          mktTemplates={mktTemplates}
        />
      </PageContainer>
    </>
  );
}

// ── Campaign Responses Panel ─────────────────────────────────────────────────

function CampaignResponsesPanel({ tenantId, campaignId }) {
  const { t } = useTranslation('common');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/campaigns/${tenantId}/${campaignId}/responses`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tenantId, campaignId]);

  if (loading) return <ResponsesSection><Spinner size="16px" /></ResponsesSection>;
  if (!data || data.total === 0) return null;

  const STATUS_EMOJI = { confirmed: '✅', cancelled: '❌', rescheduled: '🔄', unknown: '❓' };
  const STATUS_LABEL = {
    confirmed: t('campaigns.respConfirmed', { defaultValue: 'Confirmaram' }),
    cancelled: t('campaigns.respCancelled', { defaultValue: 'Cancelaram' }),
    rescheduled: t('campaigns.respRescheduled', { defaultValue: 'Reagendaram' }),
  };

  return (
    <ResponsesSection>
      <ResponsesTitle>📊 {t('campaigns.responses')} ({data.total})</ResponsesTitle>
      <ResponsesSummary>
        {Object.entries(data.summary || {}).map(([status, count]) => (
          <ResponseChip key={status} $status={status}>
            {STATUS_EMOJI[status] || '•'} {STATUS_LABEL[status] || status}: {count}
          </ResponseChip>
        ))}
      </ResponsesSummary>
      {data.responses?.length > 0 && (
        <ResponsesList>
          {data.responses.slice(0, 20).map((r, i) => (
            <ResponseItem key={i}>
              <ResponseContact>{r.contactName || r.contactId || '—'}</ResponseContact>
              <ResponseStatus $status={r.status}>{STATUS_EMOJI[r.status] || '•'} {r.status}</ResponseStatus>
              <ResponseTime>{r.timestamp ? new Date(r.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</ResponseTime>
            </ResponseItem>
          ))}
        </ResponsesList>
      )}
    </ResponsesSection>
  );
}

const ResponsesSection = styled.div`margin-top: ${theme.spacing[4]}; padding-top: ${theme.spacing[4]}; border-top: 1px solid ${theme.colors.border};`;
const ResponsesTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[3]};`;
const ResponsesSummary = styled.div`display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap; margin-bottom: ${theme.spacing[3]};`;
const ResponseChip = styled.span`font-size: ${theme.typography.xs}; padding: ${theme.spacing[1]} ${theme.spacing[2]}; border-radius: ${theme.radii.full}; background: ${p => p.$status === 'confirmed' ? '#ecfdf5' : p.$status === 'cancelled' ? '#fef2f2' : '#fffbeb'}; color: ${p => p.$status === 'confirmed' ? '#065f46' : p.$status === 'cancelled' ? '#991b1b' : '#92400e'}; font-weight: ${theme.typography.medium};`;
const ResponsesList = styled.div`display: flex; flex-direction: column; gap: 2px; max-height: 200px; overflow-y: auto;`;
const ResponseItem = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; padding: ${theme.spacing[1]} ${theme.spacing[2]}; border-radius: ${theme.radii.sm}; &:hover { background: ${theme.colors.gray50}; }`;
const ResponseContact = styled.span`flex: 1; font-size: ${theme.typography.xs}; color: ${theme.colors.textPrimary}; font-weight: ${theme.typography.medium};`;
const ResponseStatus = styled.span`font-size: ${theme.typography.xs}; color: ${p => p.$status === 'confirmed' ? theme.colors.success : p.$status === 'cancelled' ? theme.colors.danger : theme.colors.warning};`;
const ResponseTime = styled.span`font-size: 10px; color: ${theme.colors.textMuted}; white-space: nowrap;`;

const MetaRow      = styled.div`display: flex; flex-direction: column; gap: 2px; margin-bottom: ${theme.spacing[2]};`;const MetaItem     = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const EndpointUrlRow = styled.div`display: flex; align-items: center; gap: 6px; padding: ${theme.spacing[1]} ${theme.spacing[2]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md}; margin-bottom: ${theme.spacing[2]};`;
const EndpointUrlText = styled.code`flex: 1; font-size: 11px; color: ${theme.colors.textSecondary}; font-family: monospace; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const EndpointCopyBtn = styled.button`background: none; border: none; cursor: pointer; padding: 2px; color: ${theme.colors.textMuted}; border-radius: ${theme.radii.sm}; &:hover { color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; }`;
const StatsRow     = styled.div`display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap; margin-bottom: ${theme.spacing[2]};`;
const StatChip     = styled.span`font-size: ${theme.typography.xs}; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.full}; background: ${p => p.$danger ? '#fee2e2' : p.$warn ? '#fef3c7' : theme.colors.gray100}; color: ${p => p.$danger ? '#991b1b' : p.$warn ? '#92400e' : theme.colors.textSecondary};`;
const FieldHint    = styled.div`font-size: ${theme.typography.xs}; color: ${p => p.warn ? theme.colors.warning : theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;

const DetailGrid = styled.div`display: flex; flex-direction: column; gap: 0;`;
const DetailRow = styled.div`display: flex; padding: ${theme.spacing[2]} 0; border-bottom: 1px solid ${theme.colors.gray50}; &:last-child { border-bottom: none; }`;
const DetailLabel = styled.span`flex-shrink: 0; width: 160px; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; padding-top: 2px;`;
const DetailValue = styled.span`flex: 1; font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary}; word-break: break-word;`;
const DetailValueMono = styled.span`flex: 1; font-size: 11px; font-family: monospace; color: ${theme.colors.textPrimary}; background: ${theme.colors.gray50}; padding: 2px 6px; border-radius: ${theme.radii.sm}; word-break: break-all;`;

const DryRunGrid = styled.div`display: grid; grid-template-columns: repeat(4, 1fr); gap: ${theme.spacing[3]}; margin-bottom: ${theme.spacing[4]};`;
const DryRunStat = styled.div`text-align: center; padding: ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md};`;
const DryRunStatValue = styled.div`font-size: ${theme.typography.xl}; font-weight: ${theme.typography.bold}; color: ${p => p.$color === 'success' ? theme.colors.success : p.$color === 'warning' ? theme.colors.warning : p.$color === 'danger' ? theme.colors.danger : theme.colors.textPrimary};`;
const DryRunStatLabel = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px;`;
const DryRunSection = styled.div`margin-top: ${theme.spacing[4]}; padding-top: ${theme.spacing[3]}; border-top: 1px solid ${theme.colors.border};`;
const DryRunSectionTitle = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; margin-bottom: ${theme.spacing[2]};`;
const DryRunCode = styled.pre`font-size: 11px; background: ${theme.colors.gray50}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border-radius: ${theme.radii.md}; overflow-x: auto; margin: 0;`;
const DryRunSampleRow = styled.div`display: flex; justify-content: space-between; padding: ${theme.spacing[1]} ${theme.spacing[2]}; border-bottom: 1px solid ${theme.colors.gray50}; font-size: ${theme.typography.sm}; &:last-child { border-bottom: none; }`;


function formatDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

const TabBar       = styled.div`display: flex; gap: 0; border-bottom: 2px solid ${theme.colors.border}; margin-bottom: ${theme.spacing[4]};`;
const Tab          = styled.button`display: flex; align-items: center; gap: ${theme.spacing[1]}; padding: ${theme.spacing[2]} ${theme.spacing[4]}; background: none; border: none; border-bottom: 2px solid ${p => p.$active ? theme.colors.primary : 'transparent'}; margin-bottom: -2px; color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary}; font-size: ${theme.typography.sm}; font-weight: ${p => p.$active ? theme.typography.semibold : 'normal'}; cursor: pointer; transition: ${theme.transitions.fast}; &:hover { color: ${theme.colors.primary}; }`;
const TabCount     = styled.span`background: ${theme.colors.gray100}; color: ${theme.colors.textSecondary}; font-size: 11px; padding: 1px 6px; border-radius: ${theme.radii.full}; margin-left: 4px;`;
const HistoryTable = styled.table`width: 100%; border-collapse: collapse; background: white; border-radius: ${theme.radii.lg}; overflow: hidden; box-shadow: ${theme.shadows.sm}; font-size: ${theme.typography.sm};`;
const HTh          = styled.th`text-align: left; padding: ${theme.spacing[2]} ${theme.spacing[3]}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid ${theme.colors.border}; background: ${theme.colors.gray50}; white-space: nowrap;`;
const HTd          = styled.td`padding: ${theme.spacing[2]} ${theme.spacing[3]}; border-bottom: 1px solid ${theme.colors.border}; color: ${theme.colors.text}; &:last-child { border-right: none; }`;

const ScheduledInfo = styled.div`display: flex; align-items: center; gap: 6px; flex: 1; font-size: ${theme.typography.xs}; color: ${theme.colors.primary}; background: ${theme.colors.primaryLight}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[2]} ${theme.spacing[3]}; flex-wrap: wrap;`;
const NextRunLabel  = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; font-weight: ${theme.typography.medium};`;
const ModeToggle    = styled.div`display: flex; gap: 0; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; overflow: hidden; margin-bottom: ${theme.spacing[4]};`;
const ModeBtn       = styled.button`flex: 1; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${p => p.$active ? theme.colors.primary : 'white'}; color: ${p => p.$active ? 'white' : theme.colors.textSecondary}; border: none; cursor: pointer; font-size: ${theme.typography.sm}; font-weight: ${p => p.$active ? theme.typography.semibold : 'normal'}; transition: ${theme.transitions.fast};`;
const CronExamples  = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: ${theme.spacing[2]};`;
const CronChip      = styled.button`background: ${theme.colors.gray100}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.full}; padding: 3px 10px; font-size: 11px; color: ${theme.colors.textSecondary}; cursor: pointer; transition: ${theme.transitions.fast}; &:hover { background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; border-color: ${theme.colors.primary}; }`;


